/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { END } from 'redux-saga';
import {
    fork, take, call, put, select,
    takeLatest,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';

import cartServiceClient from '../../../../../../../apis/cart-service-apis/cartServiceClient';

import updateCartItemQuantity from '../../../../../../../apis/cart-service-apis/updateCartItemQuantity';
import updatePersonalizationLines from '../../../../../../../apis/cart-service-apis/updatePersonalizationLines';

import { callHandleCartFailure } from '../../Cart-Actions';
import { clearOrderItemErrors } from '../../../../../Checkout/ducks/Common/ducks/Error/Error-Actions';
import { flagOffShowLoadingMessage, updateShowLoadingMessage } from '../../../../../Checkout/ducks/Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';

import { getOrderId } from '../../Cart-Selectors';

import { retrieveCart } from '../RetrieveCart/RetrieveCart-Operations';
import { prepareRetreiveCart } from '../RetrieveCart/PrepareRetreiveCart-Operations';
import { checkJWT } from '../../../../../Member/ducks/Auth/Auth-Operations';
import { processFeatureErrors } from '../../../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { refreshCartData, updateOrder } from './UpdateCart-Actions';
import { getOrderItemErrors } from '../../../../../Checkout/ducks/Common/ducks/Error/Error-Selector';
import { setMfeBannerCodesAttempted } from '../../../Payment/Promotions/Promotion-Actions';

/**
 * @description update the order for delivery date, pmall delivery date, quantity and personalization lines
 * @param { { callback: func, data: number } } action
 * @returns updated cart or error message
 */
function* workerUpdateOrder(action) {
    try {
        // Show Site Loader
        yield put(updateShowLoadingMessage());
        const orderItemErrors = yield select(getOrderItemErrors);
        const cartId = yield select(getOrderId);

        // Clear errors new cart services
        if (action.data?.orderItemId) {
            if (orderItemErrors?.[action.data.orderItemId]?.deliveryDate) {
                yield put(clearOrderItemErrors(
                    action.data.orderItemId,
                    'deliveryDate',
                    { errType: 'clearAllFields', field: '' },
                ));
            }
            if (orderItemErrors?.[action.data.orderItemId]?.recipient) {
                yield put(clearOrderItemErrors(
                    action.data.orderItemId,
                    'recipient',
                    { errType: 'clearAllFields', field: '' },
                ));
            }
        }

        if (orderItemErrors?.[cartId]?.payment) {
            yield put(clearOrderItemErrors(
                cartId,
                'payment',
                { errType: 'clearAllFields', field: '' },
            ));
        }

        const jwtToken = yield call(checkJWT);
        const cartObj = {
            env: {},
            jwtToken,
            cartId,
            cartItemId: action.data.orderItemId,
        };
        switch (action.updateOrderProps.updateType) {
            case 'delivery':
            case 'pmallShippingMethod': {
                const deliveryDateObj = action.data?.updateDeliveryDate || {};

                cartObj.deliveryDate = deliveryDateObj.deliveryDate || null;
                cartObj.deliverySLA = deliveryDateObj.deliverySLA || null;
                cartObj.deliveryDateType = deliveryDateObj.calendarDateType || null;

                if (deliveryDateObj.calendarStartDate) {
                    cartObj.deliverRangeStartDate = deliveryDateObj.calendarStartDate;
                }

                if (action.data.zipCode) {
                    cartObj.zipCode = action.data.zipCode;
                }

                if (action.data.locationType) {
                    cartObj.locationType = action.data.locationType;
                }

                if (deliveryDateObj.holidayCode) {
                    cartObj.holidayCode = deliveryDateObj.holidayCode;
                }

                if (deliveryDateObj.attributes) {
                    cartObj.attributes = deliveryDateObj.attributes;
                }

                if (deliveryDateObj.flex?.flexOptionId) {
                    cartObj.flexOptionId = deliveryDateObj.flex?.flexOptionId;
                    cartObj.flexOptionKey = deliveryDateObj.flex?.flexOptionKey || null;
                    if (deliveryDateObj.flex?.flexVariantId) {
                        cartObj.flexVariantId = deliveryDateObj.flex?.flexVariantId || null;
                    }
                }

                yield call(cartServiceClient.updateCartItemDeliveryInfo, cartObj);
                break;
            }
            case 'quantity': {
                cartObj.quantity = action.data.quantity;
                yield call(updateCartItemQuantity, cartObj);
                break;
            }
            case 'personalization': {
                cartObj.personalizationLines = action.data.personalization;
                yield call(updatePersonalizationLines, cartObj);
                break;
            }
            default:
                return;
        }

        yield call(prepareRetreiveCart, action.updateOrderProps.page);
        yield call(retrieveCart);

        if (action.callback) {
            yield call(action.callback, false);
        }

        // Hide Site Loader
        yield put(flagOffShowLoadingMessage());
    } catch (ex) {
        yield put(flagOffShowLoadingMessage());
        yield put(callHandleCartFailure(ex));

        const featureType = action.updateOrderProps?.updateType === 'pmallShippingMethod'
            ? 'deliveryDate'
            : 'recipient';

        yield fork(processFeatureErrors, {
            data: {
                orderItemId: action.data.orderItemId,
                errorFeatureType: featureType,
                errorValues: ex.response?.data,
            },
        });

        if (ex?.response?.data?.errorKey === 'ERROR_CARTITEM_NOT_FOUND') {
            if (action.callback) {
                yield call(action.callback, false);
            }
        } else {
            yield call(retrieveCart);
        }

        mbpLogger.logError({
            action,
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            function: 'updateOrder',
            jsError: ex,
            message: 'Update Operation: update order failed.',
        });
    }
}

function* watcherUpdateOrder() {
    let action = yield take(updateOrder().type);
    while (action !== END) {
        yield call(workerUpdateOrder, action);
        action = yield take(updateOrder().type);
    }
}

function* workerRefreshCartData(action) {
    try {
        yield put(setMfeBannerCodesAttempted([]));
        yield call(prepareRetreiveCart, action.page);
        yield call(retrieveCart);
    } catch (ex) {
        mbpLogger.logError({
            action,
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'workerRefreshCartData',
            jsError: ex,
            message: 'Refresh Cart Data failed.',
        });
    }
}

function* watcherRefreshCartData() {
    yield takeLatest(refreshCartData().type, workerRefreshCartData);
}

const watchers = [
    fork(watcherRefreshCartData),
    fork(watcherUpdateOrder),
];

export { watchers };

export default {};
